/**
 * MODULE: domain/sales-intelligence/frontend/components/InsightDisplaySignalSections.tsx
 * PURPOSE: Funding / hiring / tech / news panels for InsightDisplay (presentational only).
 * DEPENDENCIES: react, contracts types
 * PUBLIC_API: InsightDisplaySignalSections
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 191 / 220 lines
 */

import React from 'react';
import type {
  FundingSignal,
  HiringSignal,
  NewsSignal,
  TechChangeSignal,
} from '../../contracts/sales-intelligence.types';

export interface InsightDisplaySignalSectionsProps {
  fundingList: FundingSignal[];
  hiringList: HiringSignal[];
  techList: TechChangeSignal[];
  newsList: NewsSignal[];
}

export function InsightDisplaySignalSections({
  fundingList,
  hiringList,
  techList,
  newsList,
}: InsightDisplaySignalSectionsProps) {
  if (
    fundingList.length === 0 &&
    hiringList.length === 0 &&
    techList.length === 0 &&
    newsList.length === 0
  ) {
    return null;
  }

  return (
    <div className="space-y-4">
      {fundingList.length > 0 && (
        <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 border border-green-100 dark:border-green-800/80">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden>
              💰
            </span>
            <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Funding activity</h5>
            <span className="text-xs text-gray-500 dark:text-gray-400">({fundingList.length})</span>
          </div>
          <ul className="space-y-3">
            {fundingList.map((f, i) => (
              <li
                key={i}
                className="text-sm text-gray-700 dark:text-gray-300 border-b border-green-100/80 dark:border-green-800/60 last:border-0 pb-3 last:pb-0"
              >
                <div className="font-medium text-gray-800 dark:text-gray-100">
                  {[f.round, f.amount].filter(Boolean).join(' · ') || 'Funding note'}
                  {f.date ? (
                    <span className="font-normal text-gray-500 dark:text-gray-400"> · {f.date}</span>
                  ) : null}
                </div>
                {f.investors.length > 0 && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Investors: {f.investors.join(', ')}
                  </p>
                )}
                {f.source_url ? (
                  <a
                    href={f.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-green-700 dark:text-green-400 hover:underline mt-1 inline-block"
                  >
                    Source
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hiringList.length > 0 && (
        <div className="bg-purple-50 dark:bg-purple-950/30 rounded-lg p-4 border border-purple-100 dark:border-purple-800/80">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden>
              👥
            </span>
            <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Hiring activity</h5>
            <span className="text-xs text-gray-500 dark:text-gray-400">({hiringList.length})</span>
          </div>
          <ul className="space-y-3">
            {hiringList.map((h, i) => (
              <li
                key={i}
                className="text-sm text-gray-700 dark:text-gray-300 border-b border-purple-100/80 dark:border-purple-800/60 last:border-0 pb-3 last:pb-0"
              >
                <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {h.department || 'Hiring signal'}
                </p>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {h.role_count > 0 && <span>Roles: {h.role_count}</span>}
                  {h.seniority_level ? <span>Seniority: {h.seniority_level}</span> : null}
                  {h.detected_at ? <span>Detected: {h.detected_at}</span> : null}
                </div>
                {h.source_url ? (
                  <a
                    href={h.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-purple-700 dark:text-purple-300 hover:underline mt-1 inline-block"
                  >
                    Source
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {techList.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 border border-blue-100 dark:border-blue-800/80">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden>
              ⚙️
            </span>
            <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Technology changes</h5>
            <span className="text-xs text-gray-500 dark:text-gray-400">({techList.length})</span>
          </div>
          <ul className="space-y-2">
            {techList.map((t, i) => (
              <li key={i} className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  {t.technology || 'Technology note'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  {t.change_type}
                  {t.detected_at ? ` · ${t.detected_at}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {newsList.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 border border-amber-100 dark:border-amber-800/80">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg" aria-hidden>
              📰
            </span>
            <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Recent news</h5>
            <span className="text-xs text-gray-500 dark:text-gray-400">({newsList.length})</span>
          </div>
          <ul className="space-y-3">
            {newsList.map((n, i) => (
              <li
                key={i}
                className="text-sm text-gray-700 dark:text-gray-300 border-b border-amber-100/80 dark:border-amber-800/60 last:border-0 pb-3 last:pb-0"
              >
                <div className="font-medium text-gray-800 dark:text-gray-100">
                  {n.headline || 'News item'}
                </div>
                {n.summary ? (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-4">{n.summary}</p>
                ) : null}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {n.published_at ? <span>{n.published_at}</span> : null}
                  <span className="capitalize">{n.sentiment}</span>
                </div>
                {n.source_url ? (
                  <a
                    href={n.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-amber-800 dark:text-amber-300 hover:underline mt-1 inline-block"
                  >
                    Open link
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
